<template>
    <button
        :class="['button-' + design, classExpression]"
        :type="type"
        :disabled="disabled"
        @click="buttonClick"
    >
        {{ text }}

        <component :is="getIconByName(icon)" v-if="icon" class="icon"></component>
    </button>
</template>

<script lang="ts">
import { PropType } from 'vue';
import AccountIcon from '@/assets/icons/account.svg';
import HeartIcon from '@/assets/icons/heart-outline.svg';
import InvoicesIcon from '@/assets/icons/invoices.svg';
import LogoutIcon from '@/assets/icons/logout.svg';
import OrderedBeforeIcon from '@/assets/icons/ordered-before.svg';
import OrdersIcon from '@/assets/icons/orders.svg';
import SearchIcon from '@/assets/icons/search-outline.svg';
import ShoppingCartIcon from '@/assets/icons/winkelmand_empty.svg';

export default {
    props: {
        design: {
            default: '',
            type: String,
            required: false,
        },
        classExpression: {
            default: () => ({}),
            required: false,
        },
        type: {
            type: String as PropType<'submit' | 'reset' | 'button'>,
            required: false,
        },
        disabled: {
            type: Boolean,
            required: false,
        },
        text: {
            type: String,
        },
        icon: {
            type: String,
            default: '',
        },
    },
    setup(_props: any, context: any) {
        function buttonClick(e: any) {
            context.emit('buttonClick', e);
        }

        const icons = [
            {
                name: 'account',
                component: AccountIcon,
            },
            {
                name: 'winkelmand_empty',
                component: ShoppingCartIcon,
            },
            {
                name: 'heart-outline',
                component: HeartIcon,
            },
            {
                name: 'ordered-before',
                component: OrderedBeforeIcon,
            },
            {
                name: 'invoices',
                component: InvoicesIcon,
            },
            {
                name: 'logout',
                component: LogoutIcon,
            },
            {
                name: 'orders',
                component: OrdersIcon,
            },
            {
                name: 'search',
                component: SearchIcon,
            },
        ];

        function getIconByName(name: string) {
            return icons.find((icon: any) => icon.name === name)?.component;
        }

        return {
            getIconByName,
            buttonClick,
        };
    },
};
</script>

<style scoped lang="scss">
@import "~@/assets/variables";

// Base styles
button {
    border: 2px solid $black;
    font-family: 'DINPro';
    font-size: 16px;
    font-weight: 500;
    font-stretch: semi-condensed;
    font-style: normal;
    line-height: 1.63;
    letter-spacing: 1px;
    text-align: center;
    text-transform: uppercase;
    padding: .11rem 1.5rem .1rem 1.5rem;
    margin-top: 0.75rem;
    height: 40px;
    min-width: 9.25rem;
    &:focus {
        outline: none;
    }

    &:disabled {
        cursor: not-allowed;
    }
}

// Variations
.button {
    &-primary {
        background: $white;
        color: $black;

        &:not(:disabled):hover{
            border: 2px solid $kwast-red;
            color: $kwast-red;
        }
    }

    &-small {
        background: $white;
        min-width: 4.375rem;
        padding: .11rem 1rem .1rem 1rem;
    }

    &-red {
        background: $kwast-red;
        color: $white;
        border-color: $kwast-red;

        &:not(:disabled):hover{
            background: $white;
            color: $kwast-red;
        }
    }
}

.icon {
    height: 1rem;
    width: auto;
    margin-bottom: 4px;
}

@media only screen and (max-width: 792px){
    button {
        padding: .11rem .5rem .1rem .5rem;
    }
}
</style>
